import { StateApiFactory } from '../types';
import { DependencyList, useEffect, useMemo, useState } from 'react';

export const useEffectAsync = (
    effect: (cleanup: { didCancel: boolean }) => Promise<void>,
    showModal: () => void,
    deps?: DependencyList
): void => {
    useEffect((): (() => void) => {
        /**
         * This cleanup object addresses the React warning below:
         *
         * "Warning: Can't perform a React state update on an unmounted component.
         * This is a no-op, but it indicates a memory leak in your application.
         * To fix, cancel all subscriptions and asynchronous tasks in a useEffect cleanup function."
         *
         * It is an object and not just a boolean variable, because we are passing it into effect(...),
         * so we need an object in order to pass it by reference.
         *
         * ALWAYS CHECK !cleanup.didCancel INSIDE THE effect CALLBACK FUNCTION BODY, BEFORE EACH UPDATE TO LOCAL STATE
         */
        const cleanup = {
            didCancel: false
        };

        effect(cleanup).catch(showModal);

        return (): void => {
            cleanup.didCancel = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);
};

/**
 * Generic function that adds state to the a component (with the useState hook), initializes the state and
 * uses a factory to return an Api (Application Programming Interface, not to confuse with a web/rest api)
 * that manages the state. We can optimize useStateApi by memoizing the execution of apiStateFactory.
 * @param stateApiFactory is a function that creates and returns a state Api
 * @param initialState is the initial state of the component
 *
 * @returns {A} an Api with the component's state properties and methods to manage them
 */
export const useStateApi = <S, A>(
    stateApiFactory: StateApiFactory<S, A>,
    initialState: S
): A => {
    const [state, setState] = useState<S>(initialState);
    return useMemo((): A => stateApiFactory({ state, setState }), [
        state,
        setState,
        stateApiFactory
    ]);
};
